import React from 'react';
import { Plane, Luggage, Clock } from 'lucide-react';
import { StorytellingLayout, ImageCarousel, ExpandableCard } from '../base';
import { getAirline } from '../../../config/airlines';

/**
 * Tela de storytelling para voos
 * 
 * Single Source of Truth: Backend (BlockEnricher/AirportRules) envia
 * origin_city e destination_city. Frontend apenas renderiza.
 */

// Imagens padrão para voos
const FLIGHT_IMAGES = {
  shared: 'https://viagemeturismo.abril.com.br/wp-content/uploads/2021/08/177598304_1919846751501628_1668980737963925542_n.jpg?quality=70&strip=info&w=1280&h=720&crop=1',
  outbound: 'https://www.infomoney.com.br/wp-content/uploads/2019/06/janela-de-aviao.jpg?w=900&quality=70&strip=all',
  return: 'https://blogdaengenharia.com/wp-content/uploads/2021/03/ucak-pencereleri-neden-yuvarlaktir-1.jpg'
};

export default function StorytellingFlight({
  block,
  isOutbound = true,
  stepIndicator,
  narrativeText,
  ctaText,
  onContinue,
  onBack,
  onShare
}) {
  const details = block.details || {};
  const segments = details.segments || [];
  const baggage = details.baggage || {};

  // Imagens baseadas no tipo de voo
  const images = block.images || [
    FLIGHT_IMAGES.shared,
    isOutbound ? FLIGHT_IMAGES.outbound : FLIGHT_IMAGES.return
  ];

  /**
   * getAirportCityName - Fallback de segurança
   * 
   * O backend (BlockEnricher/AirportRules) envia origin_city e destination_city.
   * Esta função existe apenas para dados legados que não passaram pelo enricher.
   * 
   * @deprecated Preferir usar seg.origin_city / seg.destination_city do backend
   */
  const getAirportCityName = (code) => code || '---';

  const firstSegment = segments[0] || {};
  const lastSegment = segments[segments.length - 1] || firstSegment;

  // Título com nomes das cidades
  const originCity =
    firstSegment.origin_city ||
    getAirportCityName(firstSegment.origin) ||
    'Origem';

  const destinationCity =
    lastSegment.destination_city ||
    getAirportCityName(lastSegment.destination) ||
    'Destino';

  const title = `${originCity} → ${destinationCity}`;

  // Dados da companhia aérea
  const airlineIdentifier = details.airline || firstSegment.airline;
  const airline = getAirline(airlineIdentifier);

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('pt-BR');
  };

  // Calcula tempo de conexão entre segmentos
  const calculateConnectionTime = (currentSeg, nextSeg) => {
    if (!currentSeg.arrival_time || !nextSeg.departure_time) return '35min';

    const [arrH, arrM] = currentSeg.arrival_time.split(':').map(Number);
    const [depH, depM] = nextSeg.departure_time.split(':').map(Number);

    let diffMinutes = (depH * 60 + depM) - (arrH * 60 + arrM);
    if (diffMinutes < 0) diffMinutes += 24 * 60;

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    if (hours > 0) {
      return `${hours}h${minutes > 0 ? minutes + 'min' : ''}`;
    }
    return `${minutes}min`;
  };

  // Calcula duração total
  const calculateTotalDuration = () => {
    if (details.duration) return details.duration;
    if (segments.length === 0) return 'N/A';

    const first = segments[0];
    const last = segments[segments.length - 1];

    if (!first.departure_time || !last.arrival_time) return 'N/A';

    const [depH, depM] = first.departure_time.split(':').map(Number);
    const [arrH, arrM] = last.arrival_time.split(':').map(Number);

    let diffMinutes = (arrH * 60 + arrM) - (depH * 60 + depM);
    if (diffMinutes < 0) diffMinutes += 24 * 60;

    const hours = Math.floor(diffMinutes / 60);
    const minutes = diffMinutes % 60;

    return `${hours}h ${minutes > 0 ? minutes + 'min' : ''}`.trim();
  };

  /**
   * renderBaggageIcons - Exibe ícones de bagagem
   * 
   * Single Source of Truth: Backend (BlockEnricher) envia dados completos.
   * Frontend apenas renderiza, não infere regras.
   */
  const renderBaggageIcons = () => {
    const hasPersonalItem = baggage.personal_item ?? true;
    const hasCarryOn = baggage.carry_on ?? false;
    const hasChecked = (baggage.checked_bags ?? 0) > 0;

    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        {/* Item pessoal */}
        <div style={{
          width: '44px',
          height: '44px',
          backgroundColor: hasPersonalItem ? 'rgba(93, 212, 195, 0.15)' : 'rgba(255, 255, 255, 0.08)',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none"
            stroke={hasPersonalItem ? '#5DD4C3' : 'rgba(255, 255, 255, 0.3)'}
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="6" y="9" width="12" height="12" rx="2" ry="2" />
            <path d="M9 9V7a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
            <path d="M6 13h12" />
          </svg>
        </div>

        {/* Bagagem de mão */}
        <div style={{
          width: '44px',
          height: '44px',
          backgroundColor: hasCarryOn ? 'rgba(93, 212, 195, 0.15)' : 'rgba(255, 255, 255, 0.08)',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
            stroke={hasCarryOn ? '#5DD4C3' : 'rgba(255, 255, 255, 0.3)'}
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="8" width="14" height="13" rx="2" ry="2" />
            <path d="M8 8V6a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
            <path d="M5 13h14" />
          </svg>
        </div>

        {/* Bagagem despachada */}
        <div style={{
          width: '44px',
          height: '44px',
          backgroundColor: hasChecked ? 'rgba(93, 212, 195, 0.15)' : 'rgba(255, 255, 255, 0.08)',
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
            stroke={hasChecked ? '#5DD4C3' : 'rgba(255, 255, 255, 0.3)'}
            strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="4" y="7" width="16" height="14" rx="2" ry="2" />
            <path d="M7 7V5a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v2" />
            <path d="M4 13h16" />
          </svg>
        </div>
      </div>
    );
  };

  /**
   * renderBaggageSection - Exibe seção de bagagem
   * 
   * Usa baggage_text do backend (BlockEnricher) ou fare_family como fallback.
   */
  const renderBaggageSection = () => (
    <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <Luggage size={16} color="#5DD4C3" strokeWidth={2} />
        <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>Bagagem</span>
        <span style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white' }}>
          {baggage.fare_family || details.fare_family || 'Não informada'}
        </span>
      </div>
      {renderBaggageIcons()}
    </div>
  );

  const renderHeader = () => (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
      {/* Logo da companhia ou ícone padrão */}
      <div style={{
        width: '48px',
        height: '48px',
        backgroundColor: airline?.logo ? 'white' : 'rgba(93, 212, 195, 0.15)',
        borderRadius: '0.75rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        overflow: 'hidden',
        padding: airline?.logo ? '6px' : 0
      }}>
        {airline?.logo ? (
          <img 
            src={airline.logo} 
            alt={airline.name}
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'contain'
            }}
          />
        ) : (
          <Plane size={24} color="#5DD4C3" strokeWidth={2} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: 0 }}>
          Companhia aérea
        </p>
        <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
          {airline?.name || details.airline || 'Companhia'} • {details.flight_number || 'XX 0000'}
        </p>
      </div>
      <div style={{ textAlign: 'right' }}>
        <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 0.25rem 0' }}>
          Data do voo
        </p>
        <p style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'white', margin: 0 }}>
          {formatDate(block.date)}
        </p>
      </div>
    </div>
  );

  const renderCollapsedContent = () => (
    <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 0.25rem 0' }}>
            Partida
          </p>
          <p style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'white', margin: 0 }}>
            {firstSegment.departure_time || '--:--'}
            <span style={{ fontSize: '0.9375rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', marginLeft: '0.25rem' }}>
              {firstSegment.origin || '---'} - {firstSegment.origin_city || getAirportCityName(firstSegment.origin)}
            </span>
          </p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 0.25rem 0' }}>
            Chegada
          </p>
          <p style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'white', margin: 0 }}>
            {lastSegment.arrival_time || '--:--'}
            <span style={{ fontSize: '0.9375rem', fontWeight: 400, color: 'rgba(255, 255, 255, 0.7)', marginLeft: '0.25rem' }}>
              {lastSegment.destination || '---'} - {lastSegment.destination_city || getAirportCityName(lastSegment.destination)}
            </span>
          </p>
        </div>
      </div>
    </div>
  );

  const renderExpandedSegments = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
      {segments.map((seg, idx) => (
        <React.Fragment key={idx}>
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>Partida</span>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.6)' }}>Chegada</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <span style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'white' }}>
                  {seg.departure_time}
                </span>
                <span style={{ fontSize: '0.9375rem', color: 'rgba(255, 255, 255, 0.7)', marginLeft: '0.25rem' }}>
                  {seg.origin} - {seg.origin_city || getAirportCityName(seg.origin)}
                </span>
              </div>

              <div style={{ flex: 1, margin: '0 1rem', borderTop: '1px dashed rgba(255, 255, 255, 0.3)' }} />

              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '1.0625rem', fontWeight: 700, color: 'white' }}>
                  {seg.arrival_time}
                </span>
                <span style={{ fontSize: '0.9375rem', color: 'rgba(255, 255, 255, 0.7)', marginLeft: '0.25rem' }}>
                  {seg.destination} - {seg.destination_city || getAirportCityName(seg.destination)}
                </span>
              </div>
            </div>
          </div>

          {/* Badge de conexão entre segmentos */}
          {idx < segments.length - 1 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.625rem 0.75rem',
              backgroundColor: 'rgba(93, 212, 195, 0.1)',
              borderRadius: '0.5rem'
            }}>
              <Clock size={16} color="#5DD4C3" strokeWidth={2} />
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.7)' }}>
                Conexão em {getAirportCityName(seg.destination)} • {calculateConnectionTime(seg, segments[idx + 1])} de espera
              </span>
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );

  const renderExpandedInfo = () => (
    <p style={{
      fontSize: '0.8125rem',
      color: 'rgba(255, 255, 255, 0.7)',
      lineHeight: 1.6,
      margin: '1rem 0 0 0'
    }}>
      • Duração total: {calculateTotalDuration()}<br />
      • {segments.length > 1 ? `${segments.length - 1} conexão (${segments.slice(0, -1).map(s => s.destination).join(', ')})` : 'Voo direto'}<br />
      • Check-in online disponível 48h antes
    </p>
  );

  const cardContent = (isExpanded) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {renderHeader()}

      {isExpanded ? (
        <>
          {renderExpandedSegments()}
          {renderBaggageSection()}
          {renderExpandedInfo()}
        </>
      ) : (
        <>
          {renderCollapsedContent()}
          {renderBaggageSection()}
        </>
      )}
    </div>
  );

  return (
    <StorytellingLayout
      backgroundImage={images[0]}
      title={title}
      narrativeText={narrativeText}
      stepIndicator={stepIndicator}
      ctaText={ctaText}
      onContinue={onContinue}
      onBack={onBack}
      onShare={onShare}
    >
      <ImageCarousel images={images} />
      <ExpandableCard>
        {cardContent}
      </ExpandableCard>
    </StorytellingLayout>
  );
}
